import React from 'react';
import './FaqBlock.scss';
import { AnimatePresence, m, useReducedMotion } from 'motion/react';
import FAQ, { FaqItem, FaqParagraph } from 'content/faq';

function renderFaqParagraph(paragraph: FaqParagraph): React.ReactNode {
    if (typeof paragraph === 'string') return paragraph;
    return paragraph.map((part, index) =>
        typeof part === 'string' ? (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={index}>{part}</React.Fragment>
        ) : (
            <a
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                className="rd-faq__inline-link"
                href={part.url}
                target="_blank"
                rel="noreferrer"
            >
                {part.text}
            </a>
        )
    );
}

function paragraphKey(paragraph: FaqParagraph): string {
    if (typeof paragraph === 'string') return paragraph.slice(0, 24);
    return paragraph
        .map(part => (typeof part === 'string' ? part : part.text))
        .join('')
        .slice(0, 24);
}

function FaqRow({
    item,
    open,
    onToggle,
}: {
    item: FaqItem;
    open: boolean;
    onToggle: (id: string) => void;
}): React.ReactElement {
    const reduced = useReducedMotion();
    return (
        <div className={`rd-faq__item${open ? ' rd-faq__item--open' : ''}`}>
            <button
                type="button"
                className="rd-faq__head"
                aria-expanded={open}
                aria-controls={`faq-${item.id}`}
                onClick={() => onToggle(item.id)}
            >
                <span className="rd-faq__question">{item.question}</span>
                <span className="rd-faq__toggle">
                    <span className="rd-faq__bar" />
                    <span className="rd-faq__bar" />
                </span>
            </button>
            <AnimatePresence initial={false}>
                {open ? (
                    <m.div
                        id={`faq-${item.id}`}
                        className="rd-faq__body"
                        initial={
                            reduced ? undefined : { height: 0, opacity: 0 }
                        }
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={reduced ? undefined : { height: 0, opacity: 0 }}
                        transition={{ duration: reduced ? 0 : 0.22 }}
                    >
                        <div className="rd-faq__answer">
                            {item.answer.map(paragraph => (
                                <p key={paragraphKey(paragraph)}>
                                    {renderFaqParagraph(paragraph)}
                                </p>
                            ))}
                            {item.link ? (
                                <a
                                    className="rd-faq__link"
                                    href={item.link.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {item.link.label}
                                </a>
                            ) : null}
                        </div>
                    </m.div>
                ) : null}
            </AnimatePresence>
        </div>
    );
}

interface FaqBlockProps {
    items?: FaqItem[];
}
function FaqBlock({ items = FAQ }: FaqBlockProps): React.ReactElement {
    const [openId, setOpenId] = React.useState<string | undefined>(undefined);
    const onToggle = React.useCallback((id: string) => {
        setOpenId(current => (current === id ? undefined : id));
    }, []);
    return (
        <section className="rd-block">
            <h2 className="rd-h2">Вопросы и ответы</h2>
            <div className="rd-faq">
                {items.map(item => (
                    <FaqRow
                        key={item.id}
                        item={item}
                        open={openId === item.id}
                        onToggle={onToggle}
                    />
                ))}
            </div>
        </section>
    );
}

FaqBlock.defaultProps = {
    items: FAQ,
};

export default React.memo(FaqBlock);
